import { useState, useEffect, useRef, useCallback } from 'react';
import Dropdown from '../../Components/CustomDropdown/customdropdown';
import CustomSelect from '../../Components/customs/dropdown';
import { useToast } from '../../Utils/toastContext';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { debounce } from 'lodash';
import CertificateViewer from './certificate-viewer';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { formatDateTimeUTC } from '../../Constants/helper-functions';

/* ------------------------------------------------------------------ */
/* Share history                                                     */
/* GET /api/v1/customer/certificate/{certificate_id}/shares/history  */
/* data.events[]: label, description, action, performed_at,          */
/*   performed_by, acting_role_name, previous_access_level,          */
/*   access_level, share.{share_type, viewer_email, status}          */
/* data.total / limit / page / current_page / total_pages            */
/* ------------------------------------------------------------------ */

const PAGE_SIZES = [10, 20, 30, 50];

const HISTORY_LIST_KEYS = [
  'events',
  'history',
  'share_history',
  'shares_history',
  'shares',
  'activities',
  'logs',
  'records',
  'items',
  'results',
];

const HISTORY_META_KEYS = new Set([
  'total',
  'total_count',
  'totalCount',
  'total_pages',
  'totalPages',
  'last_page',
  'page',
  'current_page',
  'limit',
  'per_page',
  'message',
  'status',
]);

const humanizeHistory = (value = '') =>
  String(value)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (m) => m.toUpperCase());

const historyLabel = (value) => {
  if (value == null || value === '') return '';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') {
    return (
      value.label ||
      value.name ||
      value.full_name ||
      [value.first_name, value.last_name].filter(Boolean).join(' ') ||
      value.title ||
      value.email ||
      value.value ||
      ''
    );
  }
  return '';
};

/* Chat clients sometimes wrap emails / dotted actions in markdown links. */
const unwrapMarkup = (value) => {
  const text = historyLabel(value);
  if (!text) return '';
  return text.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
};

const prettyHistory = (value) => {
  const text = historyLabel(value);
  if (!text) return '';
  if (/\d/.test(text)) return text;
  if (
    text.includes('_') ||
    text === text.toLowerCase() ||
    text === text.toUpperCase()
  ) {
    return humanizeHistory(text);
  }
  return text;
};

const historyPick = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (value != null && value !== '') return value;
  }
  return undefined;
};

const personFrom = (value) => {
  const text = historyLabel(value);
  if (text) return text;
  if (value && typeof value === 'object') {
    return value.email || value.username || '';
  }
  return '';
};

const ACCESS_LEVEL_LABELS = {
  1: 'View Certificate',
  2: 'View Business Details',
  3: 'View/Download Document',
};

const accessLabel = (value) => {
  if (value == null || value === '') return '';
  if (
    typeof value === 'number' ||
    (typeof value === 'string' && /^\d+$/.test(value))
  ) {
    const id = Number(value);
    return ACCESS_LEVEL_LABELS[id] || `Level ${id}`;
  }
  if (typeof value === 'object') {
    const named = prettyHistory(value);
    if (named && named !== String(value.id)) return named;
    if (value.id != null && ACCESS_LEVEL_LABELS[value.id]) {
      return ACCESS_LEVEL_LABELS[value.id];
    }
    return named || (value.id != null ? `Level ${value.id}` : '');
  }
  return prettyHistory(value);
};

const formatHistoryWhen = (value) => {
  if (value == null || value === '') return '';
  const raw =
    typeof value === 'object'
      ? value.date || value.datetime || value.label || value.value
      : value;
  if (raw == null || raw === '') return '';
  try {
    const formatted = formatDateTimeUTC(raw);
    if (formatted) return formatted;
  } catch (e) {
    /* use a local fallback below */
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return String(raw);
  return parsed.toLocaleString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDetailValue = (value) => {
  if (value == null || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') return String(value);
  if (typeof value === 'string') {
    if (
      /^\d{4}-\d{2}-\d{2}T/.test(value) ||
      /^\d{4}-\d{2}-\d{2} /.test(value)
    ) {
      return formatHistoryWhen(value) || value;
    }
    return value.length > 400 ? `${value.slice(0, 400)}…` : value;
  }
  if (Array.isArray(value)) {
    if (!value.length) return '—';
    return value
      .map((item) => prettyHistory(item) || formatDetailValue(item))
      .join(', ');
  }
  if (typeof value === 'object') {
    const label = prettyHistory(value);
    const extras = Object.entries(value)
      .filter(
        ([key, item]) =>
          !['label', 'name', 'title', 'full_name'].includes(key) &&
          item != null &&
          item !== '' &&
          typeof item !== 'object',
      )
      .slice(0, 4)
      .map(([key, item]) => `${humanizeHistory(key)}: ${item}`);
    if (label && extras.length) return `${label} · ${extras.join(' · ')}`;
    if (label) return label;
    try {
      return JSON.stringify(value);
    } catch (e) {
      return '—';
    }
  }
  return String(value);
};

const describeChanges = (changes) => {
  if (!changes || typeof changes !== 'object' || Array.isArray(changes))
    return '';
  return Object.entries(changes)
    .map(([key, value]) => {
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        ('from' in value || 'to' in value || 'old' in value || 'new' in value)
      ) {
        const from = value.from ?? value.old ?? '—';
        const to = value.to ?? value.new ?? '—';
        return `${humanizeHistory(key)}: ${prettyHistory(from) || formatDetailValue(from)} → ${prettyHistory(to) || formatDetailValue(to)}`;
      }
      return `${humanizeHistory(key)}: ${prettyHistory(value) || formatDetailValue(value)}`;
    })
    .filter(Boolean)
    .join(' · ');
};

const buildHistoryFields = (raw) => {
  if (!raw || typeof raw !== 'object') return [];
  return Object.entries(raw)
    .filter(
      ([, value]) =>
        value != null && value !== '' && typeof value !== 'function',
    )
    .map(([key, value]) => ({
      key,
      label: humanizeHistory(key),
      value: formatDetailValue(value),
    }));
};

const historyNumber = (obj, keys) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (
      typeof value === 'string' &&
      value.trim() !== '' &&
      Number.isFinite(Number(value))
    ) {
      return Number(value);
    }
  }
  return undefined;
};

const extractHistoryList = (response) => {
  const buckets = [response?.data, response];
  for (const bucket of buckets) {
    if (Array.isArray(bucket)) return bucket;
    if (!bucket || typeof bucket !== 'object') continue;
    for (const key of HISTORY_LIST_KEYS) {
      if (Array.isArray(bucket[key])) return bucket[key];
    }
    for (const value of Object.values(bucket)) {
      if (
        Array.isArray(value) &&
        (value.length === 0 || (value[0] && typeof value[0] === 'object'))
      ) {
        return value;
      }
    }
  }
  const data = response?.data;
  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const meaningful = Object.keys(data).filter(
      (key) => !HISTORY_META_KEYS.has(key),
    );
    if (
      meaningful.length &&
      meaningful.every((key) => !Array.isArray(data[key]))
    ) {
      return [data];
    }
  }
  return [];
};

const mapHistoryItem = (raw, index = 0) => {
  const record = raw || {};
  const share =
    record.share && typeof record.share === 'object' ? record.share : {};
  const actionKey =
    unwrapMarkup(
      historyPick(record, [
        'action',
        'event',
        'event_type',
        'event_name',
        'activity',
        'activity_type',
      ]),
    ) || '';
  /* API `label` is the display string ("Accepted", "Share link created"). */
  const label =
    unwrapMarkup(record.label) || prettyHistory(actionKey) || 'Update';
  const description =
    unwrapMarkup(
      historyPick(record, [
        'description',
        'details',
        'message',
        'note',
        'reason',
        'remarks',
      ]),
    ) || '';
  const name =
    personFrom(
      historyPick(record, [
        'viewer_name',
        'recipient_name',
        'shared_with_name',
        'shared_with',
        'name',
      ]),
    ) ||
    personFrom(record.viewer) ||
    personFrom(share.viewer) ||
    personFrom(share.viewer_user) ||
    '';
  const email =
    unwrapMarkup(
      share.viewer_email ||
        historyPick(record, [
          'viewer_email',
          'recipient_email',
          'shared_with_email',
          'email',
        ]),
    ) ||
    share.viewer?.email ||
    '—';
  const actor =
    personFrom(record.performed_by) ||
    personFrom(
      historyPick(record, [
        'actor',
        'created_by',
        'updated_by',
        'shared_by',
        'performed_by_name',
      ]),
    ) ||
    '';
  const role = unwrapMarkup(record.acting_role_name) || '';
  const whenRaw = historyPick(record, [
    'performed_at',
    'created_at',
    'occurred_at',
    'timestamp',
    'action_at',
    'shared_at',
    'updated_at',
    'date',
  ]);
  const fromAccess = accessLabel(record.previous_access_level);
  const access = accessLabel(record.access_level);
  const accessText =
    fromAccess && access && fromAccess !== access
      ? `${fromAccess} → ${access}`
      : access || fromAccess || '';
  const status =
    prettyHistory(share.status) ||
    prettyHistory(record.status) ||
    unwrapMarkup(share.status?.name) ||
    '';
  const shareType = prettyHistory(
    share.share_type ||
      historyPick(record, ['share_type', 'method', 'channel']),
  );
  const changeSummary = describeChanges(
    record.changes || record.change || record.diff,
  );

  return {
    id: record.id ?? record.history_id ?? record.event_id ?? `history-${index}`,
    label,
    action: label,
    actionKey,
    description,
    name,
    email,
    actor,
    role,
    when: formatHistoryWhen(whenRaw) || '—',
    access: accessText,
    status,
    shareType,
    shareId: record.share_id ?? share.id ?? '',
    viewerUserId: share.viewer_user_id ?? record.viewer_user_id ?? '',
    details: description,
    ip:
      historyLabel(historyPick(record, ['ip_address', 'ip', 'client_ip'])) ||
      '',
    changeSummary,
    fields: buildHistoryFields(record),
    raw: record,
  };
};

const activityTone = (action) => {
  const text = String(action || '').toLowerCase();
  if (/revok|delet|remov|expir|block|fail|deny|reject/.test(text)) return 'red';
  if (/accept/.test(text)) return 'green';
  if (/view|open|read/.test(text)) return 'slate';
  if (/download/.test(text)) return 'violet';
  if (/updat|edit|access|permission|chang/.test(text)) return 'yellow';
  if (/cop(y|ied)|link|creat|share|invit|grant|sent/.test(text)) return 'brand';
  return 'brand';
};

const statusTone = (status) => {
  const text = String(status || '').toLowerCase();
  if (/revok|expir|fail|block|delet|remov|inactiv|reject/.test(text))
    return 'red';
  if (/pend|invit|review|progress|sent/.test(text)) return 'yellow';
  if (/active|complete|success|grant|accept/.test(text)) return 'green';
  return 'slate';
};

const historyInitials = (name, email) => {
  if (name && name !== '—') {
    const parts = name
      .replace(/[^a-zA-Z0-9 ]/g, ' ')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  const local = String(email || '').split('@')[0];
  if (!local || email === '—') return '•';
  return local.slice(0, 2).toUpperCase();
};

const historyPageWindow = (count, current) => {
  if (count <= 1) return [1];
  const size = Math.min(count, 5);
  const start = Math.max(1, Math.min(count - size + 1, current - 2));
  return Array.from({ length: size }, (_, index) => start + index);
};

const HISTORY_TONE = {
  brand: 'bg-[#375AE7]/10 text-[#375AE7] ring-[#375AE7]/15',
  green: 'bg-green-50 text-green-700 ring-green-100',
  yellow: 'bg-yellow-50 text-yellow-700 ring-yellow-100',
  red: 'bg-red-50 text-red-600 ring-red-100',
  slate: 'bg-slate-100 text-slate-600 ring-slate-200',
  violet: 'bg-violet-50 text-violet-700 ring-violet-100',
};

function HistoryChip({ tone = 'slate', children }) {
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ${HISTORY_TONE[tone] || HISTORY_TONE.slate}`}
    >
      <span className="truncate">{children}</span>
    </span>
  );
}

function HistoryIcon({ name }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
  };
  if (name === 'search') {
    return (
      <svg {...common}>
        <circle cx="11" cy="11" r="7" />
        <path d="m20 20-3.5-3.5" />
      </svg>
    );
  }
  if (name === 'refresh') {
    return (
      <svg {...common}>
        <path d="M20 12a8 8 0 1 1-2.3-5.7" />
        <path d="M20 4v6h-6" />
      </svg>
    );
  }
  if (name === 'close') {
    return (
      <svg {...common}>
        <path d="M6 6l12 12M18 6 6 18" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 8v4l2.5 2" />
    </svg>
  );
}

function ShareHistorySection({
  certificate,
  rows,
  loading,
  loaded,
  error,
  page,
  totalPages,
  total,
  limit,
  query,
  onQueryChange,
  onPageChange,
  onLimitChange,
  onRefresh,
  detail,
  onOpenDetail,
  onCloseDetail,
}) {
  const safePages = Math.max(1, totalPages || 1);
  const safePage = Math.min(page || 1, safePages);
  const rangeStart = rows.length ? (safePage - 1) * limit + 1 : 0;
  const rangeEnd = rows.length ? rangeStart + rows.length - 1 : 0;
  const certificateLabel =
    certificate?.certificate_number || certificate?.id || '—';

  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') onCloseDetail();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [detail, onCloseDetail]);

  const summary = detail
    ? [
        ['When', detail.when],
        ['Event', detail.label || detail.action],
        ['Description', detail.description],
        ['Action', detail.actionKey],
        ['Performed by', detail.actor],
        ['Role', detail.role],
        ['Shared with', detail.email],
        ['Viewer user id', detail.viewerUserId],
        ['Share id', detail.shareId],
        ['Share type', detail.shareType],
        ['Access level', detail.access],
        ['Status', detail.status],
        ['Changes', detail.changeSummary],
      ].filter(([, value]) => value != null && value !== '' && value !== '—')
    : [];

  return (
    <section
      id="share-history"
      className="mt-4 overflow-hidden rounded-2xl border border-[#DCE3F9] bg-white shadow-lg"
    >
      <div className="border-b border-[#DCE3F9] bg-gradient-to-r from-[#F7F9FF] via-white to-[#EEF1FC] px-4 py-5 sm:px-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-[#375AE7]/10 text-[#375AE7] ring-4 ring-[#375AE7]/5">
              <HistoryIcon />
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                Audit trail
              </p>
              <h3 className="mt-0.5 text-lg font-semibold tracking-tight text-slate-800">
                Share history
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                Certificate #{certificateLabel} · every share, access change,
                and revoke.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#375AE7] ring-1 ring-[#DCE3F9]">
              {loaded
                ? `${total} ${total === 1 ? 'event' : 'events'}`
                : 'Loading'}
            </span>
            <button
              type="button"
              onClick={onRefresh}
              disabled={loading}
              className="inline-flex h-10 items-center gap-1.5 rounded-xl border border-[#DCE3F9] bg-white px-3 text-xs font-semibold text-[#375AE7] shadow-sm transition hover:bg-[#F7F9FF] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <span className={loading ? 'animate-spin' : ''}>
                <HistoryIcon name="refresh" />
              </span>
              Refresh
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 sm:px-5">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
              <HistoryIcon name="search" />
            </span>
            <input
              type="text"
              value={query}
              onChange={(event) => onQueryChange(event.target.value)}
              placeholder="Search email, event, description…"
              className="h-11 w-full rounded-xl border border-[#DCE3F9] bg-white pl-10 pr-9 text-sm text-slate-700 outline-none transition placeholder:text-slate-400 focus:border-[#375AE7] focus:ring-2 focus:ring-[#375AE7]/20"
            />
            {query ? (
              <button
                type="button"
                onClick={() => onQueryChange('')}
                title="Clear search"
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg text-slate-400 hover:bg-[#F7F9FF] hover:text-slate-600"
              >
                <HistoryIcon name="close" />
              </button>
            ) : null}
          </div>
          <div className="flex items-center gap-2 text-[12px] font-semibold text-slate-500">
            Show
            <CustomSelect
              value={limit}
              options={PAGE_SIZES.map((size) => ({
                value: size,
                label: String(size),
              }))}
              onChange={(value) => onLimitChange(Number(value))}
              ariaLabel="Rows per page"
              buttonClassName="h-auto rounded-lg px-2 py-1.5 text-[12px] font-semibold text-slate-600"
            />
          </div>
        </div>

        <div className="overflow-hidden rounded-xl border border-[#DCE3F9]">
          {loading || !loaded ? (
            <div className="divide-y divide-[#EEF2FB]" aria-busy="true">
              {[0, 1, 2, 3].map((row) => (
                <div key={row} className="flex items-center gap-4 px-4 py-4">
                  <div className="h-9 w-9 animate-pulse rounded-full bg-[#E7ECFA]" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-40 animate-pulse rounded-full bg-[#E7ECFA]" />
                    <div className="h-2.5 w-56 animate-pulse rounded-full bg-[#F1F4FC]" />
                  </div>
                  <div className="hidden h-6 w-24 animate-pulse rounded-full bg-[#E7ECFA] sm:block" />
                </div>
              ))}
            </div>
          ) : error && rows.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <p className="text-sm font-semibold text-slate-800">
                Couldn’t load share history
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                {error}
              </p>
              <button
                type="button"
                onClick={onRefresh}
                className="mt-4 inline-flex h-10 items-center rounded-xl bg-[#375AE7] px-4 text-sm font-semibold text-white hover:bg-[#2f4bd1]"
              >
                Try again
              </button>
            </div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-14 text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#375AE7]/10 text-[#375AE7] ring-8 ring-[#375AE7]/5">
                <HistoryIcon />
              </div>
              <p className="mt-4 text-sm font-semibold text-slate-800">
                {query
                  ? 'No history matches this search'
                  : 'No share history yet'}
              </p>
              <p className="mx-auto mt-1 max-w-sm text-sm leading-6 text-slate-500">
                {query
                  ? 'Try another name, email, or activity.'
                  : 'Events appear here after this certificate is shared, updated, or revoked.'}
              </p>
            </div>
          ) : (
            <>
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[760px] border-collapse text-left">
                  <thead>
                    <tr className="bg-[#F7F9FF] text-[11px] font-bold uppercase tracking-wider text-[#375AE7]">
                      <th scope="col" className="px-4 py-3 font-bold">
                        When
                      </th>
                      <th scope="col" className="px-4 py-3 font-bold">
                        Event
                      </th>
                      <th scope="col" className="px-4 py-3 font-bold">
                        Shared with
                      </th>
                      <th scope="col" className="px-4 py-3 font-bold">
                        Shared Type
                      </th>
                      <th scope="col" className="px-4 py-3 font-bold">
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EEF2FB]">
                    {rows.map((row) => (
                      <tr
                        key={row.id}
                        className="transition-colors hover:bg-[#F7F9FF]"
                      >
                        <td className="px-4 py-3.5 align-top">
                          <p className="whitespace-nowrap text-sm font-medium text-slate-700">
                            {row.when}
                          </p>
                          {row.actor ? (
                            <p
                              className="mt-0.5 max-w-[180px] truncate text-[11px] text-slate-400"
                              title={`by ${row.actor}${row.role ? ` · ${row.role}` : ''}`}
                            >
                              by {row.actor}
                              {row.role ? ` · ${row.role}` : ''}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          <HistoryChip
                            tone={activityTone(row.label || row.actionKey)}
                          >
                            {row.label || row.action}
                          </HistoryChip>
                          {row.description ? (
                            <p
                              className="mt-1 max-w-[280px] text-[11px] leading-4 text-slate-500 line-clamp-2"
                              title={row.description}
                            >
                              {row.description}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          <div className="flex min-w-0 items-center gap-2.5">
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#375AE7]/10 text-[11px] font-bold text-[#375AE7]">
                              {historyInitials(row.name, row.email)}
                            </span>
                            <div className="min-w-0">
                              <p
                                className="max-w-[220px] truncate text-sm font-semibold text-slate-800"
                                title={row.email}
                              >
                                {row.email}
                              </p>
                              {row.shareType ? (
                                <p className="mt-0.5 text-[11px] text-slate-400">
                                  {row.shareType}
                                  {row.shareId ? ` · #${row.shareId}` : ''}
                                </p>
                              ) : null}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          {row.shareType ? (
                            <p className="mt-0.5 text-[11px] ">
                              {row.shareType}
                            </p>
                          ) : null}
                        </td>
                        <td className="px-4 py-3.5 align-top">
                          {row.status ? (
                            <HistoryChip tone={statusTone(row.status)}>
                              {row.status}
                            </HistoryChip>
                          ) : (
                            <span className="text-sm text-slate-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="divide-y divide-[#EEF2FB] md:hidden">
                {rows.map((row) => (
                  <button
                    type="button"
                    key={row.id}
                    onClick={() => onOpenDetail(row)}
                    className="block w-full px-4 py-4 text-left transition hover:bg-[#F7F9FF]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-2.5">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#375AE7]/10 text-[11px] font-bold text-[#375AE7]">
                          {historyInitials(row.name, row.email)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-800">
                            {row.email}
                          </p>
                          <p className="truncate text-xs text-slate-500">
                            {row.label || row.action}
                          </p>
                        </div>
                      </div>
                      <HistoryChip
                        tone={activityTone(row.label || row.actionKey)}
                      >
                        {row.label || row.action}
                      </HistoryChip>
                    </div>
                    {row.description ? (
                      <p className="mt-2 line-clamp-2 text-[11px] leading-4 text-slate-500">
                        {row.description}
                      </p>
                    ) : null}
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-slate-400">
                      <span>{row.when}</span>
                      {row.status ? (
                        <HistoryChip tone={statusTone(row.status)}>
                          {row.status}
                        </HistoryChip>
                      ) : null}
                      {row.access ? <span>{row.access}</span> : null}
                    </div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {loaded && rows.length > 0 && (
          <div className="mt-4 flex flex-col items-center justify-between gap-3 sm:flex-row">
            <p className="text-center text-sm text-[#8C8C8C] sm:text-left">
              Showing{' '}
              <span className="font-semibold text-slate-600">
                {rangeStart}–{rangeEnd}
              </span>{' '}
              of <span className="font-semibold text-slate-600">{total}</span>{' '}
              events
            </p>
            {safePages > 1 && (
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <button
                  type="button"
                  disabled={safePage === 1}
                  onClick={() => onPageChange(safePage - 1)}
                  className="h-9 rounded-lg border border-[#DCE3F9] bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-[#F7F9FF] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Prev
                </button>
                {historyPageWindow(safePages, safePage).map((pageNumber) => (
                  <button
                    type="button"
                    key={pageNumber}
                    onClick={() => onPageChange(pageNumber)}
                    className={`h-9 min-w-9 rounded-lg px-3 text-xs font-bold transition ${
                      safePage === pageNumber
                        ? 'bg-[#375AE7] text-white shadow-md'
                        : 'border border-[#DCE3F9] bg-white text-slate-600 hover:bg-[#F7F9FF]'
                    }`}
                  >
                    {pageNumber}
                  </button>
                ))}
                <button
                  type="button"
                  disabled={safePage === safePages}
                  onClick={() => onPageChange(safePage + 1)}
                  className="h-9 rounded-lg border border-[#DCE3F9] bg-white px-3 text-xs font-semibold text-slate-600 hover:bg-[#F7F9FF] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  Next
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-[80] flex justify-end bg-slate-900/50 p-0 sm:p-3">
          <button
            type="button"
            aria-label="Close share history details"
            className="absolute inset-0 cursor-default"
            onClick={onCloseDetail}
          />
          <div className="relative ml-auto flex h-full w-full max-w-md flex-col overflow-hidden rounded-none bg-white shadow-2xl sm:rounded-2xl">
            <div className="border-b border-[#DCE3F9] bg-gradient-to-r from-[#F7F9FF] via-white to-[#EEF1FC] px-5 py-5">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                    Share event
                  </p>
                  <h3 className="mt-1 truncate text-lg font-semibold text-slate-800">
                    {detail.label || detail.action}
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">{detail.when}</p>
                </div>
                <button
                  type="button"
                  onClick={onCloseDetail}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[#DCE3F9] bg-white text-slate-500 hover:bg-[#F7F9FF]"
                  aria-label="Close"
                >
                  <HistoryIcon name="close" />
                </button>
              </div>
              <div className="mt-4 flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#375AE7]/10 text-xs font-bold text-[#375AE7]">
                  {historyInitials(detail.name, detail.email)}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-800">
                    {detail.email}
                  </p>
                  <p className="truncate text-xs text-slate-500">
                    {detail.actor
                      ? `by ${detail.actor}${detail.role ? ` · ${detail.role}` : ''}`
                      : detail.shareType || 'Share'}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-5 py-5">
              <dl className="grid grid-cols-1 gap-3">
                {summary.map(([label, value]) => (
                  <div
                    key={label}
                    className="rounded-xl border border-[#EEF2FB] bg-[#F7F9FF]/60 px-3.5 py-3"
                  >
                    <dt className="text-[10px] font-bold uppercase tracking-wider text-[#375AE7]">
                      {label}
                    </dt>
                    <dd className="mt-1 break-words text-sm text-slate-700">
                      {value}
                    </dd>
                  </div>
                ))}
              </dl>
              {detail.fields?.length > 0 && (
                <details className="mt-4 rounded-xl border border-[#DCE3F9] bg-white px-3.5 py-3">
                  <summary className="cursor-pointer text-xs font-semibold text-[#375AE7]">
                    All fields
                  </summary>
                  <dl className="mt-3 space-y-3">
                    {detail.fields.map((field) => (
                      <div key={field.key}>
                        <dt className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                          {field.label}
                        </dt>
                        <dd className="mt-0.5 break-words text-sm text-slate-700">
                          {field.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </details>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const CertificateManagement = () => {
  const BASE_URL = process.env.REACT_APP_BASE_URL;
  const { showToast } = useToast();
  const roleName = useSelector((state) => state.user.roleName);
  const [isOpenShare, setisOpenShare] = useState(false);
  const [isOpenManageAccess, setisOpenManageAccess] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [link, setLink] = useState(null);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [selectedCertificateId, setSelectedCertificateId] = useState(null);

  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [tableData, setTableData] = useState([]);
  const [open, setOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [certUrl, setCertUrl] = useState(null);
  const [certificateId, setCertificateId] = useState(null);

  const [email, setEmail] = useState('');
  const [showRecords, setShowRecords] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(10);
  const [totalCount, setTotalCount] = useState(10);
  const [isLoadingCard, setIsLoadingCard] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  /* Share history — GET /certificate/{id}/shares/history */
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyError, setHistoryError] = useState('');
  const [historyQuery, setHistoryQuery] = useState('');
  const [historySearch, setHistorySearch] = useState('');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit, setHistoryLimit] = useState(10);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyDetail, setHistoryDetail] = useState(null);
  const [historyNonce, setHistoryNonce] = useState(0);
  const historyReq = useRef(0);

  const filterOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 30, label: '30' },
    { value: 50, label: '50' },
  ];
  const [preSelectedFilter, setpreSelectedFilter] = useState({
    value: 10,
    label: '10',
  });
  const status = 'active';
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const togglePopupShare = (card) => {
    setisOpenShare(!isOpenShare);
    setEmail('');
    setCurrentCard(card);
  };
  const togglePopupManageAccess = (user) => {
    setCurrentUser(user);
    setSelectedCertificateId(selectedCertificate?.id);

    // ✅ build permissions from API
    const mappedPermissions = buildPermissionsFromAPI(user);
    setPermissions(mappedPermissions);

    setisOpenManageAccess(true);
  };
  const handleFilterChange = (selectedOption) => {
    console.log('Selected Option:', selectedOption);
    setShowRecords(parseInt(selectedOption.value));
    setCurrentPage(1);
  };
  const [permissions, setPermissions] = useState([
    { id: 1, label: 'View Certificate', enabled: true },
    { id: 2, label: 'View  Business Details', enabled: false },
    { id: 3, label: 'View/Download Document', enabled: false },
  ]);

  const togglePermission = (id) => {
    setPermissions((prev) =>
      prev.map((p) => ({
        ...p,
        enabled: p.id === id, // only one selected
      })),
    );
  };
  const getAccessLevel = () => {
    const selected = permissions.find((p) => p.enabled);
    return selected ? selected.id : 1; // default fallback
  };
  const formatDate = (dateString) => {
    return dateString.split(' ')[0];
  };
  const GetAPIcalls = async (url) => {
    const token = localStorage.getItem('jwtToken');
    const apiUrl = `${BASE_URL}${url}`;
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      console.error('API Call Error:', error); // Log full error for debugging
      throw new Error(
        typeof error.message === 'string'
          ? error.message
          : 'Network error, please try again later',
      );
    }
  };
  const handleUpdateApplication = useCallback(
    debounce(async (e, cardId) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await GetAPIcalls(
          `/api/v1/customer/certificate/${cardId}/view-url`,
        );
        if (response.status == 200) {
          setCertUrl(response.data.view_url);
          setCertificateId(cardId);
          setOpen(true);
        } else {
          toastMessage = { message: response.message, type: 'error' };
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 1000); // Small delay if needed
        }
      }
    }, 1000),
    [], // Dependencies
  );
  const resetApplicationAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const accessLevel = getAccessLevel();

    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/customer/shares/${currentUser.id}/access`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: headers,
        body: JSON.stringify({
          access_level: accessLevel,
        }),
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleSaveAccess = async () => {
    let toastMessage = null;
    try {
      const response = await resetApplicationAPI();
      if (response.status == 200) {
        showToast('Access updated', 'success');

        setisOpenManageAccess(false);
        fetchAccessData(selectedCertificateId);
        setHistoryNonce((n) => n + 1);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };

  const buildPermissionsFromAPI = (user) => {
    const accessId = user?.access_level?.id;

    return [
      {
        id: 1,
        label: 'View Certificate',
        enabled: accessId === 1,
      },
      {
        id: 2,
        label: 'View  Business Details',
        enabled: accessId === 2,
      },
      {
        id: 3,
        label: 'View/Download Document',
        enabled: accessId === 3,
      },
    ];
  };
  useEffect(() => {
    if (!selectedCertificate) {
      setTableData([]);
      setHistoryRows([]);
      setHistoryTotal(0);
      setHistoryDetail(null);
    }
  }, [selectedCertificate]);
  useEffect(() => {
    if (certificates.length > 0) {
      setSelectedCertificate(certificates[0]);
      setSelectedCertificateId(certificates[0].id);
      setHistoryPage(1);
      setHistoryQuery('');
      setHistorySearch('');
      setHistoryDetail(null);
    }
  }, [certificates]);
  const fetchAccessData = async (certificateId) => {
    setIsLoading(true);

    try {
      const res = await GetAPIcalls(
        `/api/v1/customer/certificate//${certificateId}/shares?page=${currentPage}&limit=${showRecords}&search=${searchQuery}`,
      );

      const mappedData = res.data.shares.map((item) => ({
        id: item.id,
        name: item.viewer_name || 'Not available',
        email: item.viewer_email || 'Not available',
        access_level: item.access_level,
        share_type: item.share_type,
        status: item.status,
      }));
      setTableData(mappedData);
      setTotalPages(res.data.total_pages);
      setTotalCount(res.data.total);
    } catch (err) {
      showToast(err.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    if (selectedCertificate) {
      fetchAccessData(selectedCertificate.id);
    }
  }, [selectedCertificate, currentPage, showRecords, searchQuery]);

  const fetchShareHistory = async (certificateId) => {
    if (!certificateId) return;
    const seq = ++historyReq.current;
    const page = historyPage;
    const limit = historyLimit;
    const search = historySearch;
    setHistoryLoading(true);
    setHistoryError('');
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(limit));
      if (search) params.set('search', search);
      const res = await GetAPIcalls(
        `/api/v1/customer/certificate/${certificateId}/shares/history?${params.toString()}`,
      );
      if (seq !== historyReq.current) return;
      if (typeof res?.status === 'number' && res.status !== 200) {
        throw new Error(res.message || 'Could not load share history');
      }

      let list = extractHistoryList(res).map(mapHistoryItem);
      const data = res?.data && typeof res.data === 'object' ? res.data : res;
      const apiTotal = historyNumber(data, [
        'total',
        'total_count',
        'totalCount',
      ]);
      const apiPages = historyNumber(data, [
        'total_pages',
        'totalPages',
        'last_page',
      ]);
      const apiPage = historyNumber(data, ['page', 'current_page']);
      const serverPaged =
        apiTotal != null || apiPages != null || apiPage != null;

      if (!serverPaged && search) {
        const q = search.toLowerCase();
        list = list.filter((row) =>
          [
            row.label,
            row.actionKey,
            row.description,
            row.name,
            row.email,
            row.actor,
            row.role,
            row.access,
            row.status,
            row.shareType,
            row.when,
          ]
            .join(' ')
            .toLowerCase()
            .includes(q),
        );
      }

      let nextTotal = apiTotal ?? list.length;
      let nextPages =
        apiPages ?? Math.max(1, Math.ceil(nextTotal / limit) || 1);
      let nextRows = list;
      if (!serverPaged || list.length > limit) {
        const start = (Math.max(page, 1) - 1) * limit;
        nextRows = list.slice(start, start + limit);
        if (!serverPaged) {
          nextTotal = list.length;
          nextPages = Math.max(1, Math.ceil(nextTotal / limit) || 1);
        }
      }

      setHistoryRows(nextRows);
      setHistoryTotal(nextTotal);
      setHistoryTotalPages(nextPages);
      setHistoryLoaded(true);
    } catch (err) {
      if (seq !== historyReq.current) return;
      setHistoryRows([]);
      setHistoryTotal(0);
      setHistoryTotalPages(1);
      setHistoryError(err.message || 'Could not load share history');
      setHistoryLoaded(true);
      showToast(err.message, 'error');
    } finally {
      if (seq === historyReq.current) setHistoryLoading(false);
    }
  };

  const openHistoryFor = (emailAddress) => {
    const next = (emailAddress || '').trim();
    if (next === 'Not available') {
      setHistoryQuery('');
      setHistorySearch('');
    } else {
      setHistoryQuery(next);
      setHistorySearch(next);
    }
    setHistoryPage(1);
    setisOpenManageAccess(false);
    setCurrentUser(null);
    window.setTimeout(() => {
      document.getElementById('share-history')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 60);
  };

  useEffect(() => {
    const t = window.setTimeout(() => {
      setHistoryPage(1);
      setHistorySearch(historyQuery.trim());
    }, 400);
    return () => window.clearTimeout(t);
  }, [historyQuery]);

  useEffect(() => {
    if (!selectedCertificate?.id) {
      setHistoryRows([]);
      setHistoryTotal(0);
      setHistoryTotalPages(1);
      setHistoryLoaded(false);
      setHistoryError('');
      return;
    }
    fetchShareHistory(selectedCertificate.id);
  }, [
    selectedCertificate?.id,
    historyPage,
    historyLimit,
    historySearch,
    historyNonce,
  ]);

  const handleHistoryLimit = (value) => {
    setHistoryLimit(value);
    setHistoryPage(1);
  };
  const getCertificates = async () => {
    try {
      const response = await GetAPIcalls(`/api/v1/customer/certificates`);

      if (response.status == 200) {
        const certificateData = Array.isArray(response.data)
          ? response.data
          : [];
        setCertificates(certificateData);
        if (certificateData.length === 0) {
          setSelectedCertificate(null);
          setSelectedCertificateId(null);
          setTableData([]);
          setCertUrl(null);
          setCertificateId(null);
          setOpen(false);
        }
      } else {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
        }
      }
    } catch (err) {
      showToast(err.message, 'error');
    }
  };
  const downloadExcelReportHistoricalPerformance = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/pdf',
    };
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    try {
      const response = await fetch(
        `${BASE_URL}/api/v1/customer/certificate/${currentCard.id}/download-pdf`,
        {
          method: 'GET',
          headers,
        },
      );

      if (!response.ok) {
        if (response.status === 401) {
          // Optional: clear token and redirect or notify
          localStorage.removeItem('jwtToken');
          navigate('/login');
          // Example: redirect to login page
          // window.location.href = "/login";

          // Or throw a custom error
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.blob();
    } catch (error) {
      throw new Error(error.message || 'Something went wrong');
    }
  };
  const postInviteAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/customer/certificate/${currentCard.id}/share`;
    const bodyData = {
      email: email,
    };

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleAddEmail = useCallback(
    debounce(async (e) => {
      e.preventDefault();
      let toastMessage = null;
      try {
        const response = await postInviteAPI();
        if (response.status === 201) {
          toastMessage = { message: response.message, type: 'success' };
          setHistoryNonce((n) => n + 1);
          if (currentCard?.id) fetchAccessData(currentCard.id);
        }
      } catch (error) {
        toastMessage = { message: error.message, type: 'error' };
      } finally {
        setLoading(false); // Ensure `loading` is false first
        // Show toast after loading is false
        if (toastMessage) {
          setTimeout(() => {
            showToast(toastMessage.message, toastMessage.type);
          }, 300); // Small delay if needed
        }
      }
    }, 1000),
    [email, currentCard], // Dependencies
  );
  const deleteApplicationAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const accessLevel = getAccessLevel();

    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    let apiUrl = `${BASE_URL}/api/v1/customer/shares/${currentUser.id}`;
    let bodyData = {};
    try {
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: headers,
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('jwtToken');
          navigate('/login');
          throw new Error('Session expired. Please log in again.');
        }
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));
        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleRevokeAccess = async () => {
    let toastMessage = null;
    try {
      const response = await deleteApplicationAPI();
      if (response.status == 200) {
        showToast('Access revoked successfully', 'success');

        setisOpenManageAccess(false);
        setCurrentUser(null);

        // Refresh table
        fetchAccessData(selectedCertificateId);
        setHistoryNonce((n) => n + 1);
      } else {
        toastMessage = { message: response.message, type: 'error' };
      }
    } catch (err) {
      toastMessage = { message: err.message, type: 'error' };
    } finally {
      setLoading(false); // Ensure `loading` is false first
      // Show toast after loading is false
      if (toastMessage) {
        setTimeout(() => {
          showToast(toastMessage.message, toastMessage.type);
        }, 300); // Small delay if needed
      }
    }
  };
  const postCopyAPI = async () => {
    const token = localStorage.getItem('jwtToken');
    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const apiUrl = `${BASE_URL}/api/v1/customer/certificate/${currentCard.id}/share-link`;
    const bodyData = {};

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(bodyData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({
          message: 'An unexpected error occurred',
        }));

        throw new Error(errorData.message || 'Something went wrong');
      }

      return await response.json();
    } catch (error) {
      throw new Error(error.message || 'Network error, please try again later');
    }
  };
  const handleCopy = async () => {
    try {
      setLoading(true);

      const response = await postCopyAPI();
      const link = response?.data?.link;
      setLink(link);
      setHistoryNonce((n) => n + 1);
      if (!link) throw new Error('No link found');
    } catch (err) {
      console.error(err);
      showToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  const handleDownload = useCallback(
    debounce(async () => {
      try {
        const csvBlob = await downloadExcelReportHistoricalPerformance();
        const url = window.URL.createObjectURL(csvBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Certificate.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (err) {
        showToast(err.message, 'error');
      } finally {
        setTimeout(function () {
          setLoading(false); // Ensure `loading` is false first
        }, 100);
      }
    }, 1000), // Debounce time
    [currentCard], // Dependencies
  );
  useEffect(() => {
    getCertificates();
  }, []);
  useEffect(() => {
    if (currentCard && currentCard.id) {
      handleCopy();
    }
  }, [currentCard]);
  return (
    <div className="pt-4  lg:min-w-[79vw] lg:max-w-[80vw]">
      {loading && (
        <div className="fixed top-0 left-0 w-full h-full flex justify-center items-center bg-black bg-opacity-50 z-[999]">
          <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
      {isLoadingCard ? (
        <div className="shimmer-container-list mb-4 ">
          {/* Creating 5 rows of shimmer effect */}
          {[...Array(2)].map((_, rowIndex) => (
            <div key={rowIndex} className="shimmer-row-list">
              {/* Creating 7 cells in each row for shimmer */}
              {[...Array(2)].map((_, colIndex) => (
                <div key={colIndex} className="shimmer-cell-list h-20">
                  <div className="shimmer-loader"></div>
                </div>
              ))}
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="w-full p-4 bg-white rounded-lg shadow-lg lg:min-w-[78vw] lg:max-w-[80vw]">
            {/* Header */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-medium text-gray-800">
                Certificate Management
              </h2>
              <div className="h-[1px] bg-gray-300 mt-2"></div>
            </div>

            {certificates.length > 0 ? (
              <>
                {/* Main Certificate Card */}
                <div className="w-full">
                  <div
                    className={`
      flex gap-4 pb-2
      ${certificates.length > 3 ? 'overflow-x-auto scrollbar-hide' : 'flex-wrap'}
    `}
                  >
                    {certificates.map((card, index) => {
                      const isScrollable = certificates.length > 3;

                      return (
                        <div
                          key={card.id}
                          onClick={() => {
                            if (selectedCertificate?.id !== card.id) {
                              setHistoryPage(1);
                              setHistoryQuery('');
                              setHistorySearch('');
                              setHistoryDetail(null);
                              setHistoryRows([]);
                              setHistoryLoaded(false);
                              setHistoryError('');
                            }
                            setSelectedCertificate(card);
                          }}
                          className={`
            bg-[#1E201D] text-white rounded-2xl p-6 shadow-xl border border-gray-700
${selectedCertificate?.id === card.id ? 'ring-2 ring-blue-500' : 'border-gray-700'}
            ${
              isScrollable
                ? 'min-w-[300px] sm:min-w-[320px] md:min-w-[350px] flex-shrink-0'
                : certificates.length === 1
                  ? 'w-full'
                  : certificates.length === 2
                    ? 'w-full sm:w-[calc(50%-0.5rem)]'
                    : 'w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.33%-0.67rem)]'
            }
          `}
                        >
                          {/* Header */}
                          <div className="flex justify-between items-start mb-4">
                            <div>
                              <p className="text-sm text-gray-300">Issued By</p>
                              <h3 className="text-lg font-medium">
                                {card.issued_by.first_name}{' '}
                                {card.issued_by.last_name}
                              </h3>
                            </div>

                            <img
                              src="/Ratified-logo-blue.svg"
                              alt="logo"
                              className="w-14 h-14"
                            />
                          </div>

                          <div className="border-t border-gray-600 mb-4"></div>

                          {/* Content */}
                          <div className="flex justify-between items-center">
                            {/* Date */}
                            <div>
                              <p className="text-sm text-gray-300">
                                Creation date
                              </p>
                              <p className="text-md font-medium">
                                {formatDateTimeUTC(card.verification_date)}
                              </p>
                            </div>

                            {/* Status */}
                            <div>
                              <p className="text-sm text-gray-300">Status</p>
                              <div className="flex items-center gap-2">
                                <span
                                  className={`material-symbols-outlined text-lg ${
                                    card.status.label === 'active'
                                      ? 'text-green-500'
                                      : 'text-red-500'
                                  }`}
                                >
                                  fiber_manual_record
                                </span>

                                <p className="text-md font-medium capitalize">
                                  {card.status.label}
                                </p>
                              </div>
                            </div>
                            <button
                              className="underline"
                              onClick={(e) => {
                                setLoading(true);
                                handleUpdateApplication(e, card.id);
                              }}
                            >
                              View Certificate
                            </button>
                            {/* QR */}
                            {/* <div className="bg-white p-2 rounded-md">
                          <img src="/qr.svg" alt="qr" className="w-14 h-14" />
                        </div> */}
                          </div>

                          <div className="border-t border-gray-600 mt-4 mb-4"></div>

                          {/* Button */}
                          <button
                            onClick={() => togglePopupShare(card)}
                            className="mt-4 w-full bg-gradient-to-r from-[#375AE7] to-[#375AE7] text-white py-3 rounded-xl flex items-center justify-center gap-2 shadow-lg hover:opacity-90 transition"
                          >
                            <span className="material-symbols-outlined">
                              share
                            </span>
                            Share Certificate
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="w-full mt-4 mb-2 p-4 bg-white rounded-lg shadow-lg">
                  <div className="flex justify-between items-center mb-4 border-b pb-5 border-gray-100">
                    <div className="flex items-center">
                      <img
                        src="/money-receive.svg"
                        alt="Company Logo"
                        className="mr-3"
                      />
                      <span className="font-medium lg:text-xl text-sm">
                        Access Management
                        {selectedCertificate && (
                          <span className="text-xs text-gray-500 ml-2">
                            (Certificate #
                            {selectedCertificate.certificate_number})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between flex-wrap mb-4 gap-4 mb-4">
                    <div className="flex items-left flex-col lg:flex-row flex-wrap  gap-4 lg:space-x-4 lg:space-y-0 space-x-0">
                      <div className="flex items-center space-x-2">
                        <span className="lg:text-md  text-sm text-[#8C8C8C]">
                          Show Records
                        </span>
                        <Dropdown
                          label=""
                          options={filterOptions}
                          preselectedValue={preSelectedFilter}
                          onSelect={handleFilterChange}
                        />
                      </div>
                      <div className="flex items-center">
                        <div className="relative">
                          <input
                            type="text"
                            className="p-2 pl-8 border rounded-xl w-full"
                            placeholder="Search Name"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                          />
                          <span className="absolute left-2 top-3 text-gray-500">
                            {' '}
                            <img
                              src="/search-normal.svg"
                              alt="Company Logo"
                              className="mr-2"
                            />
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="overflow-x-auto mt-4">
                    <table className=" border-collapse font-medium w-full text-xs">
                      <thead>
                        <tr className="bg-[#ECFFEE] text-[#375AE7]">
                          <th className="border cursor-pointer min-w-[100px] text-left px-4 py-2">
                            <div className="flex justify-between items-center w-full">
                              <span>No.</span>
                            </div>
                          </th>
                          <th className="border cursor-pointer min-w-60 text-left px-4 py-2">
                            <div className="flex justify-between items-center w-full">
                              <span>Name</span>
                            </div>
                          </th>
                          <th className="border cursor-pointer min-w-40  text-left px-4 py-2">
                            <div className="flex justify-between items-center w-full">
                              <span>Email</span>
                            </div>
                          </th>
                          <th className="border cursor-pointer text-left min-w-[220px] px-4 py-2">
                            <div className="flex justify-between items-center w-full">
                              <span>Action</span>
                            </div>
                          </th>
                        </tr>
                      </thead>
                      {isLoading ? (
                        <tbody className="shimmer-container-list ">
                          {/* Creating 5 rows of shimmer effect */}
                          {[...Array(2)].map((_, rowIndex) => (
                            <tr key={rowIndex} className="shimmer-row-list">
                              {/* Creating 7 cells in each row for shimmer */}
                              {[...Array(11)].map((_, colIndex) => (
                                <td
                                  key={colIndex}
                                  className="shimmer-cell-list"
                                >
                                  <div className="shimmer-loader"></div>
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      ) : (
                        <tbody>
                          {tableData.length > 0 ? (
                            tableData.map((row, index) => (
                              <tr key={row.id} className="text-black">
                                <td className="border px-4 py-2">
                                  {(index + 1).toString().padStart(2, '0')}
                                </td>{' '}
                                <td className="border px-4 py-2">{row.name}</td>
                                <td className="border px-4 py-2">
                                  {row.email}
                                </td>
                                <td className="border px-4 py-2 ">
                                  <div className="flex flex-wrap gap-2">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        togglePopupManageAccess(row);
                                      }}
                                      className="px-3 py-1  bg-[#375AE7] rounded-lg shadow-md border text-white font-medium"
                                    >
                                      Manage Access
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        openHistoryFor(
                                          row.email &&
                                            row.email !== 'Not available'
                                            ? row.email
                                            : '',
                                        )
                                      }
                                      className="px-3 py-1 rounded-lg border border-[#DCE3F9] text-[#375AE7] font-medium hover:bg-[#F7F9FF]"
                                    >
                                      History
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td className="text-center py-4" colSpan="7">
                                No results found
                              </td>
                            </tr>
                          )}
                        </tbody>
                      )}
                    </table>
                  </div>

                  {tableData.length > 0 && (
                    <div className="flex flex-wrap justify-between items-center mt-6 mb-4 space-y-4 sm:space-y-0">
                      <div className="text-[#8C8C8C] text-sm sm:text-base w-full sm:w-auto text-center sm:text-left">
                        Showing {showRecords * (currentPage - 1) + 1} to{' '}
                        {currentPage === totalPages || totalPages == 0
                          ? totalCount
                          : Math.max(
                              showRecords * currentPage,
                              tableData.length,
                            )}{' '}
                        of {totalCount} entries
                      </div>

                      <div className="flex flex-wrap items-center justify-center sm:justify-end font-semibold text-sm gap-2 w-full sm:w-auto">
                        <button
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage((prev) => prev - 1)}
                          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Prev
                        </button>
                        {Array.from({ length: Math.min(totalPages, 5) }).map(
                          (_, index) => {
                            const startPage = Math.max(
                              1,
                              Math.min(totalPages - 4, currentPage - 2),
                            );
                            const page = startPage + index;

                            return (
                              <button
                                key={page}
                                onClick={() => setCurrentPage(page)}
                                className={`px-3 py-1 rounded ${
                                  currentPage === page
                                    ? 'bg-[#375AE7] text-white'
                                    : 'bg-white border border-gray-300 text-black'
                                }`}
                              >
                                {page}
                              </button>
                            );
                          },
                        )}
                        <button
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage((prev) => prev + 1)}
                          className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <ShareHistorySection
                  certificate={selectedCertificate}
                  rows={historyRows}
                  loading={historyLoading}
                  loaded={historyLoaded}
                  error={historyError}
                  page={historyPage}
                  totalPages={historyTotalPages}
                  total={historyTotal}
                  limit={historyLimit}
                  query={historyQuery}
                  onQueryChange={setHistoryQuery}
                  onPageChange={setHistoryPage}
                  onLimitChange={handleHistoryLimit}
                  onRefresh={() => setHistoryNonce((n) => n + 1)}
                  detail={historyDetail}
                  onOpenDetail={setHistoryDetail}
                  onCloseDetail={() => setHistoryDetail(null)}
                />
              </>
            ) : (
              <div className="flex min-h-[430px] items-center justify-center rounded-2xl border border-[#DCE3F9] bg-gradient-to-br from-[#F7F9FF] via-white to-[#EEF1FC] px-6 py-12">
                <div className="mx-auto max-w-md text-center">
                  <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-[#375AE7]/10 text-[#375AE7] shadow-sm ring-8 ring-[#375AE7]/5">
                    <svg
                      width="34"
                      height="34"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.7"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M7 3.75h7l3.5 3.5v13H7a2 2 0 0 1-2-2v-12.5a2 2 0 0 1 2-2Z" />
                      <path d="M14 3.75v4h3.5M8.5 12h5M8.5 15.5h4" />
                      <path d="M16.5 16.5 20 20M20 16.5 16.5 20" />
                    </svg>
                  </div>
                  <p className="mt-6 text-xs font-bold uppercase tracking-[0.16em] text-[#375AE7]">
                    Certificate Management
                  </p>
                  <h3 className="mt-2 text-2xl font-semibold tracking-tight text-slate-800">
                    No certificates available
                  </h3>
                  <p className="mt-3 text-sm leading-6 text-slate-500">
                    Certificates will appear here once one has been issued to
                    your account. Access management will become available at
                    that time.
                  </p>
                  <div className="mx-auto mt-6 h-1 w-16 rounded-full bg-[#375AE7]" />
                </div>
              </div>
            )}
            {certUrl && certificateId && (
              <CertificateViewer
                url={certUrl}
                isOpen={open}
                certificateId={certificateId}
                onClose={() => setOpen(false)}
              />
            )}
          </div>
        </>
      )}
      {isOpenShare && currentCard && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg w-[90%] md:w-[700px] p-6">
            {/* Modal Header */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-center w-full">
                Share Certificate
              </h2>
              <button onClick={() => setisOpenShare(false)}>
                <img src="/add.svg" alt="Close" className="w-10 h-10" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="grid grid-cols-1 gap-6 items-center">
              <div className="space-y-4 mx-auto ">
                <div className="mb-4">
                  <label
                    htmlFor="email"
                    className="block dark:text-gray-300 font-normal color-test text-sm mb-1"
                  >
                    Email<span className="text-red-500">*</span>
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="text"
                    value={email}
                    autoComplete="off"
                    placeholder="example@gmail.com"
                    className={`rounded-lg h-[2.813rem] dark:text-white text-black text-base font-medium  appearance-none  w-full py-2 px-3 leading-tight focus:outline-none ${
                      email != '' && !validateEmail(email)
                        ? 'border-[#FF5050] border-2'
                        : 'border'
                    }`}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {email != '' && !validateEmail(email) && (
                    <label
                      htmlFor="confirmpassword"
                      className="flex mt-1 items-center font-normal text-xs text-[#FF5050] mb-1"
                    >
                      <img
                        src="/danger.svg"
                        alt="alert Icon"
                        className="h-4 w-4 mr-2"
                      />
                      Please enter a valid email address.
                    </label>
                  )}
                </div>
                <div className="mb-4">
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      if (!validateEmail(email)) {
                        showToast(
                          'Please enter a valid email address.',
                          'error',
                        );
                        return;
                      }
                      setLoading(true);
                      handleAddEmail(e);
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-2 w-full rounded-lg bg-[#375AE7] text-white hover:bg-gray-800 transition"
                  >
                    <span className="material-icons text-white text-[20px]">
                      share
                    </span>
                    Share Certificate via Email
                  </button>
                </div>
                <div className="mb-4">
                  <button
                    onClick={() => {
                      setLoading(true);
                      handleDownload();
                    }}
                    className="flex items-center justify-center gap-2 px-6 py-2 w-full rounded-lg bg-[#375AE7] text-white hover:bg-gray-800 transition"
                  >
                    <span className="material-icons text-white text-[20px]">
                      download
                    </span>
                    Download Certificate
                  </button>
                </div>

                <div className="mb-4">
                  <CopyToClipboard text={link}>
                    <button
                      onClick={(e) => {
                        showToast('Copied successfully', 'success');
                      }}
                      className="flex items-center justify-center gap-2 px-6 py-2 w-full rounded-lg bg-[#375AE7] text-white hover:bg-gray-800 transition"
                    >
                      <span className="material-icons text-white text-[20px]">
                        content_copy
                      </span>
                      Copy Certificate Link
                    </button>
                  </CopyToClipboard>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {isOpenManageAccess && currentUser != null && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-black/60  z-50 p-4">
          <div className="bg-white text-black rounded-2xl w-full max-w-md md:max-w-lg p-6 shadow-xl">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-semibold">{currentUser.name}</h2>

              <button
                onClick={() => {
                  setisOpenManageAccess(false);
                  setCurrentUser(null);
                }}
              >
                <span className="material-icons text-black">close</span>
              </button>
            </div>
            <p className="text-sm text-gray-500">{currentUser.email}</p>

            <div className="text-xs text-gray-400 mt-1">
              Type: {currentUser.share_type}
            </div>

            <div className="text-xs mt-1">
              Status:
              <span
                className={`ml-1 ${
                  currentUser.status?.label === 'pending'
                    ? 'text-yellow-500'
                    : 'text-green-500'
                }`}
              >
                {currentUser.status?.label}
              </span>
            </div>
            <button
              type="button"
              onClick={() =>
                openHistoryFor(
                  currentUser.email && currentUser.email !== 'Not available'
                    ? currentUser.email
                    : '',
                )
              }
              className="mt-3 inline-flex items-center text-xs font-semibold text-[#375AE7] hover:underline"
            >
              View share history
            </button>
            {/* Permissions */}
            <div className="space-y-5">
              {permissions.map((perm) => (
                <label
                  key={perm.id}
                  className="flex items-center justify-between cursor-pointer"
                >
                  <p className="text-md text-black">{perm.label}</p>

                  <input
                    type="radio"
                    name="accessLevel"
                    checked={perm.enabled}
                    onChange={() => togglePermission(perm.id)}
                    className={`flex items-center confirm-check justify-between cursor-pointer p-2 rounded-lg ${
                      perm.enabled ? 'bg-blue-50' : ''
                    }`}
                  />
                </label>
              ))}
            </div>

            {/* Buttons */}
            <div className="flex justify-between items-center mt-8 gap-4">
              {/* Revoke on left */}
              <button
                onClick={handleRevokeAccess}
                className="flex items-center gap-2 px-4 py-2 rounded-xl 
               bg-red-50 text-red-600 hover:bg-red-100 transition font-medium"
              >
                <span className="material-icons text-sm">delete</span>
                Revoke
              </button>

              {/* Right side actions */}
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setisOpenManageAccess(false);
                    setCurrentUser(null);
                  }}
                  className="px-6 py-2 rounded-full text-gray-600 border border-gray-300 hover:bg-gray-100"
                >
                  Cancel
                </button>

                <button
                  onClick={() => {
                    setLoading(true);
                    handleSaveAccess();
                  }}
                  className="px-6 py-2 rounded-full text-white bg-[#375AE7] hover:bg-[#2f4bd1]"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificateManagement;
